import React, { useEffect, useRef, useState } from "react";
import { stringToHslColor, getDarkerHslColor } from '../../helper/extension';
import { createDoctorsArray } from '../../helper/doctorHelper';
import { Image, MainSearch, QuestionnaireAlertPopup } from '../../components/componentIndex';
import { useNavigate } from "react-router-dom";
import PatientService from '../../services/API/patientService';
import useDebounce from '../../hooks/useDebounce';
import StarIcon from '../../assets/images/dashboard/star.svg';
import AOS from 'aos';
import 'aos/dist/aos.css';
import ExploreArticles from "pages/exploreArticles/exploreArticles";
import { useAuthenticationContext } from "services/slices/AuthenticationSlicer";
import MarkLocationIcon from '../../assets/images/doctorProfile/map-marker.svg';

const Dashboard = () => {
    const navigate = useNavigate();
    const [topDoctors, setTopDoctors] = useState([]);
    const [isFetching, setIsFetching] = useState(false);
    const [questionNaireComplete, setQuestionNaireComplete] = useState({});
    const [isQuestCompletePopup, setIsQuestCompletePopup] = useState(false);
    const { state: data } = useAuthenticationContext();
    const popupRef = useRef(null);

    useEffect(() => {
        AOS.init({
            offset: 200, // Trigger animations 200px from the top
            delay: 100, // Delay animations by 100ms
            duration: 500, // Animation duration in milliseconds
            easing: 'ease-in-out', // Default easing for animations
            once: false, // Whether animations should only happen once
            mirror: false, // Whether animations should happen on scroll up
        });
        localStorage.removeItem('emailAddress');
        localStorage.removeItem('phoneNumber');
        localStorage.removeItem('skippedQuestionIndex');
        const accessToken = data?.userData?.result?.token;
        if (accessToken) {
            getIsQuestionNaireComplete(accessToken);
        }
    }, []);

    const getIsQuestionNaireComplete = async (accessToken) => {
        try {
            const response = await PatientService.getIsQuestionNaireComplete(accessToken);
            if (response?.statusCode === 200) {
                const questionnaire = response?.result || {};
                setQuestionNaireComplete(questionnaire);
                if (!questionnaire?.isQuestionnaireCompleted && questionnaire?.appointmentId) {
                    setIsQuestCompletePopup(true);
                }
            }
        } catch (error) {
            console.error('Error fetching top doctors:', error);
        }
    }

    //Get Top Doctors
    const getTopDoctors = async () => {
        try {

            const response = await PatientService.getTopDoctors();
            if (response?.statusCode === 200) {
                const doctors = response?.result?.doctors || [];
                if (doctors?.length > 0) {
                    const transformedDoctors = createDoctorsArray(doctors);
                    setTopDoctors(transformedDoctors);
                    setIsFetching(false);
                }
            }
        } catch (error) {
            console.error('Error fetching top doctors:', error);
        } finally {
            setIsFetching(false);
        }
    };

    const debouncedGetTopDoctors = useDebounce(getTopDoctors, 300);

    useEffect(() => {
        if (topDoctors?.length === 0) {
            setIsFetching(true);
            debouncedGetTopDoctors();
        }
    }, [topDoctors?.length]);

    const getAosEffect = (index, type, isMobileBreakpoint = 768) => {
        const isMobile = window.innerWidth <= isMobileBreakpoint;
        if (type === "doctor") {
            return (Math.floor(index / 2) % 2 === 0)
                ? (isMobile ? "fade-top" : "fade-right")
                : (isMobile ? "fade-bottom" : "fade-left");
        }
        return ""; // Default case, if needed
    };

    // Usage:
    const aosTopDoctorEffect = (index) => getAosEffect(index, "doctor");

    const handleDoctorProfile = (topDoctorData) => {
        const currentDate = new Date();
        const queryParamsStr = "&fkUserId=" + topDoctorData?.fkUserId;
        // const queryParamsStr = "date=" + currentDate + "&fkUserId=" + topDoctorData?.fkUserId;
        navigate(`/doctor-profile${'?' + queryParamsStr}`);
    }

    const handleSearch = (searchData) => {
        const queryParamsStr = "?place=" + searchData.place + "&lat=" + searchData.lat + "&lng=" + searchData.lng + "&startDate=" + searchData.startDate + "&endDate=" + searchData.endDate + "&insurance=" + searchData.insurance + "&insId=" + searchData.insuranceId+ '#Patient';
        navigate('/search-result' + queryParamsStr);
        // navigate('/search' + queryParamsStr);
    }

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setIsQuestCompletePopup(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [setIsQuestCompletePopup]);

    const getRating = (rating) => {
        if(rating){
        }
        // ? doctor?.doctorInfo?.rating?.toFixed(1) : "N/A"
    }

    return (

        <div className="custom-container">
            {/* Header Section */}
            <div className="doctor-info-header">
                <h1 className="mb-0 text-capitalize">
                    {data?.userData?.result?.first_name || data?.userData?.result?.last_name
                        ? `Hello ${data?.userData?.result?.first_name || ''} ${data?.userData?.result?.last_name || ''},`
                        : 'Hello,'}

                </h1>
                <h2>Ready to schedule your procedure?</h2>
            </div>

            {/* Search Section */}
            <div className="dashboard-search">
                <MainSearch onChange={handleSearch} isLaning={true} />
            </div>

            {/* Top Doctors Section */}
            <section className="top-doctors">
                <h3 className="title">Top doctors</h3>
                <div className="doctor-list row">
                    {topDoctors?.length > 0 ? (
                        <>
                            {topDoctors.map((doctor, index) => (
                                <div className="col-12 col-sm-6 col-md-6 col-lg-3" key={index}>
                                    <div className="doctor-card" data-aos={aosTopDoctorEffect(index)}
                                        data-aos-anchor-placement="top-bottom" data-aos-offset="200">
                                        <div className="doctor-info">
                                            {doctor.doctorInfo.photo ? (
                                                <div className="top-doctors-profile cursor-pointer" onClick={() => handleDoctorProfile(doctor?.doctorInfo)}
                                                    style={{ backgroundImage: `url(${doctor.doctorInfo.photo})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
                                                    {doctor.doctorInfo.rating !== '0 (0)' && doctor.doctorInfo.rating !== '' && (
                                                        <div className="rating">
                                                            <span className="rating-number">{doctor.doctorInfo.rating}</span>
                                                            <Image imgUrl={StarIcon} altText={'star'} className={'star-icon'} />
                                                        </div>
                                                    )}

                                                </div>
                                            ) : (
                                                <>
                                                    <div className="top-doctors-profile top-doctor-shortname cursor-pointer" onClick={() => handleDoctorProfile(doctor?.doctorInfo)}
                                                        style={{
                                                            backgroundColor: stringToHslColor(
                                                                `${doctor?.doctorInfo?.firstName?.charAt(0) ?? ""}${doctor?.doctorInfo?.lastName?.charAt(0) ?? ""}`
                                                            ),
                                                            border: `2px solid ${getDarkerHslColor(
                                                                `${doctor?.doctorInfo?.firstName?.charAt(0) ?? ""}${doctor?.doctorInfo?.lastName?.charAt(0) ?? ""}`
                                                            )}`,
                                                        }}
                                                    >
                                                        <p className="short-name">{`${doctor?.doctorInfo?.firstName?.charAt(0) ?? ""}${doctor?.doctorInfo?.lastName?.charAt(0) ?? ""}`}</p>
                                                        {doctor?.doctorInfo.rating !== '0 (0)' && doctor?.doctorInfo?.rating !== '' && (
                                                            <div className="rating">
                                                                <span className="rating-number">{doctor?.doctorInfo?.rating}</span>
                                                                <Image imgUrl={StarIcon} altText={'star'} className={'star-icon'} />
                                                            </div>
                                                        )}

                                                    </div>
                                                </>
                                            )}
                                            <div className="doctor-details">
                                                <h4 className="name">{doctor?.doctorInfo?.name}</h4>
                                                <p className="specialty">{doctor?.doctorInfo?.specialization}</p>
                                                <p className="location text-wrap">{doctor?.doctorInfo?.organizationName}</p>
                                                <p className="location text-wrap address">{`${doctor?.doctorInfo?.cityName}, ${doctor?.doctorInfo?.stateName}`}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </>
                    ) : (
                        <>
                            {isFetching ? (
                                <>
                                    {Array.from({ length: 4 }).map((_, index) => (
                                        <div className="col-12 col-sm-6 col-md-6 col-lg-3" key={index}>
                                            <div className="doctor-card skeleton">
                                                <div className="doctor-info">
                                                    <div className="top-doctors-profile cursor-pointer skeleton-bg"></div>
                                                    <h4 className="skeleton-title"></h4>
                                                    <p className="skeleton-text"></p>
                                                    <p className="skeleton-text"></p>
                                                    <p className="skeleton-text"></p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            ) : (
                                <div className="content">No top doctors yet</div>
                            )}

                        </>
                    )}
                </div>
            </section>
            <ExploreArticles isDashboard={true} />

            {isQuestCompletePopup && (
                <div className="fullscreen-overlay">
                    <div className={`fullscreen-overlay-content alert-fullscreen-overlay alert-popup `} ref={popupRef}>
                        <QuestionnaireAlertPopup questionnaireComplete={questionNaireComplete} setIsQuestCompletePopup={setIsQuestCompletePopup} />
                    </div>
                </div>
            )}
        </div >

    );
};

export default Dashboard;
